import { createTheme, DEFAULT_THEME, SegmentedControl } from '@mantine/core';
import { produce } from 'immer';

import type { ChartGlowConfigInput } from '#core/components/dashboard/panels/chart/glow.schema.ts';

export const theme = createTheme(
  produce(DEFAULT_THEME, (draft) => {
    draft.fontFamily = 'Mona Sans';
    draft.fontFamilyMonospace = 'Geist Mono';
    draft.autoContrast = true;
    draft.defaultRadius = 'xs';
    draft.cursorType = 'pointer';
    draft.primaryColor = 'blue';
    draft.primaryShade = { dark: 8, light: 6 };

    const darkColor = '#3584e4';
    const darkFactors = [
      [0.93, 1.61],
      [0.76, 0.86],
      [0.69, 0.93],
      [0.65, 0.93],
      [0.6, 1.33],
      [0.4, 1.43],
      [0.35, 1.43],
      [0.2, 1.03],
      [0.15, 1.06],
      [0.1, 1.0],
    ] as const;

    const darkAccentColor = '#3584e4';
    const darkAccentFactors = [
      [0.95, 0.9],
      [0.95, 0.9],
      [0.85, 0.9],
      [0.75, 0.9],
      [0.6, 1.1],
      [0.45, 1.1],
      [0.4, 1.1],
      [0.35, 0.9],
      [0.28, 0.9],
      [0.22, 0.9],
    ] as const;

    draft.components.SegmentedControl = SegmentedControl.extend({
      defaultProps: {
        color: 'blue',
      },
    });

    draft.other = {
      backgroundDark: 'oklch(from var(--mantine-color-dark-9) calc(l * 0.5) c h)',
      darkHueOffset: 0,
      darkAccentHueOffset: -41,
      color: {
        dark: darkFactors.map(
          ([lFactor, cFactor]) =>
            `oklch(from ${darkColor} ${lFactor} calc(c * ${cFactor * 0.5}) calc(h + var(--theme-dark-hue-offset, 0)))`,
        ),
        darkAccent: darkAccentFactors.map(
          ([lFactor, cFactor]) =>
            `oklch(from ${darkAccentColor} ${lFactor} calc(c * ${cFactor}) calc(h + var(--theme-dark-accent-hue-offset, 0)))`,
        ),
      },
      fontWidth: {
        md: 100,
        lg: 110,
        xl: 120,
        sm: 95,
        xs: 80,
      },
      backgroundVideoDisplay: 'none',
      chartGlow: {
        lines: { enabled: true, intensity: 'high' },
        areas: { enabled: true, intensity: 'medium' },
        dots: { enabled: true, intensity: 'low' },
      } satisfies ChartGlowConfigInput,
    };

    draft.colors.dark = Array.from(
      { length: darkFactors.length },
      (_, i) => `var(--theme-color-dark-${i + 1})`,
    ) as (typeof draft.colors)[string];
    draft.colors['dark-accent'] = Array.from(
      { length: darkAccentFactors.length },
      (_, i) => `var(--theme-color-dark-accent-${i + 1})`,
    ) as (typeof draft.colors)[string];
  }),
);
